import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Tag, Select, message, Popconfirm } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const LookalikeAudiences = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [seeds, setSeeds] = useState([]);
  const [seed, setSeed] = useState(undefined);
  const [size, setSize] = useState(1);
  const [creating, setCreating] = useState(false);
  const { token } = useAuth();

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Seed', dataIndex: 'seed_name', key: 'seed_name' },
    { title: 'Similarity %', dataIndex: 'similarity_pct', key: 'similarity_pct', width: 140 },
    { title: 'Size (est)', dataIndex: 'size_estimate', key: 'size_estimate', render: v => v?.toLocaleString?.() || v, width: 140 },
    { title: 'Status', dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'ready' ? 'green' : 'blue'}>{s}</Tag>, width: 120 },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 200 },
    {
      title: 'Actions', key: 'actions', width: 140, render: (_, record) => (
        <Space>
          <Popconfirm title="Delete this lookalike?" onConfirm={() => onDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        setLoading(true);
        // Load seeds from custom audiences
        const [seedRes, lalRes] = await Promise.all([
          fetch('/api/custom-audiences', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/lookalike-audiences', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!seedRes.ok) throw new Error('Failed to load seeds');
        if (!lalRes.ok) throw new Error('Failed to load lookalikes');
        const seedData = await seedRes.json();
        const lalData = await lalRes.json();
        setSeeds(Array.isArray(seedData) ? seedData : []);
        setRows(Array.isArray(lalData) ? lalData : []);
      } catch (e) {
        message.error(e?.message || 'Failed to load lookalike data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const onCreate = async () => {
    try {
      if (!token) throw new Error('Not authenticated');
      if (!seed) return message.warning('Select a seed audience');
      setCreating(true);
      const res = await fetch('/api/lookalike-audiences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ seed_id: seed, size_pct: size }),
      });
      if (!res.ok) throw new Error('Failed to create lookalike');
      const created = await res.json();
      setRows(prev => [created, ...prev]);
      message.success('Lookalike creation started');
    } catch (e) {
      message.error(e?.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (record) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/lookalike-audiences/${record.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      setRows(prev => prev.filter(r => r.id !== record.id));
      message.success('Lookalike deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Audience' }, { title: 'Lookalike' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Lookalike Audiences</Title>
        <Space>
          <Select placeholder="Seed audience" style={{ width: 260 }} value={seed} onChange={setSeed} allowClear showSearch optionFilterProp="label">
            {seeds.map(s => (
              <Option key={s.id} value={s.id} label={s.name}>{s.name}</Option>
            ))}
          </Select>
          <Select placeholder="Size %" style={{ width: 120 }} value={size} onChange={setSize}>
            <Option value={1}>1%</Option>
            <Option value={2}>2%</Option>
            <Option value={5}>5%</Option>
          </Select>
          <Button type="primary" onClick={onCreate} loading={creating}>Create Lookalike</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Build and manage lookalike audiences from seed lists.</Paragraph>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>
    </div>
  );
};

export default LookalikeAudiences;
